import Button from "@ui/button/Button"
import { useState, type ReactNode } from "react"
import styled from "styled-components"
import { systemPost } from "src/services/superAdminApi"
import { T } from "../adminTheme"
import { Callout, date, Muted, Row, StatusBadge } from "./adminUi"

type Text = string | null | undefined
/** kyc.summary from system/user/overview (Superadmin_User_KYC_FE.md §3). The backend masks personal data; it is shown as is. */
export interface IKycSummary {
	required?: boolean
	status?: string
	source?: string
	source_label?: string
	copied_from_holder_uid?: Text
	vendor?: { provider?: Text; reference_id?: Text } | null
	verification?: { uid?: Text; module_reference?: Text; status?: Text; reason?: Text; created_at?: Text; resolved_at?: Text; basis?: Text } | null
	person?: { first_name?: Text; middle_name?: Text; last_name?: Text; gender?: Text; birth_date_masked?: Text; age?: number | null; place_of_birth?: Text } | null
	document?: { type?: Text; country?: Text; number_masked?: Text; issue_date?: Text; expiry_date?: Text; expired?: boolean | null; issuing_authority?: Text } | null
	address?: { country?: Text; city?: Text; line1_masked?: Text; line2_masked?: Text; apartment_masked?: Text; zip_masked?: Text } | null
	history?: { total?: number; approved?: number; declined?: number; expired?: number; pending?: number; under_review?: number; last_declined_reason?: Text; last_declined_at?: Text } | null
	has_module_session?: boolean
	notes?: string[]
}

const Columns = styled.div`
	display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px;
	section { border: 1px solid ${T.border}; border-radius: 10px; padding: 12px 14px; background: ${T.surface}; }
	h4 { margin: 0 0 8px; font-size: 12px; text-transform: uppercase; letter-spacing: .05em; color: ${T.muted}; font-weight: 600; }
	dl { display: grid; grid-template-columns: auto 1fr; gap: 5px 12px; margin: 0; font-size: 13.5px; }
	dt { color: ${T.muted}; }
	dd { margin: 0; min-width: 0; overflow-wrap: anywhere; font-weight: 500; }
`
const Pill = styled.span<{ $tone?: "danger" }>`
	display: inline-block; padding: 1px 7px; margin-left: 6px; border-radius: 999px; font-size: 11.5px; font-weight: 600;
	color: ${({ $tone }) => $tone ? T.danger : T.muted}; background: ${({ $tone }) => $tone ? T.dangerSoft : T.surface2};
`
const dash = (value: unknown) => value === null || value === undefined || value === "" ? "—" : String(value)
const day = (value: Text) => value ? value.slice(0, 10) : "—"
const Pairs = ({ rows }: { rows: Array<[string, ReactNode]> }) => <dl>{rows.map(([label, value]) => <div key={label} style={{ display: "contents" }}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>

/** History in one line: "2 attempts · 1 approved · 1 declined (last: 2026-10-13, reason)". */
export const historyLine = (history: NonNullable<IKycSummary["history"]>) => {
	const total = history.total ?? 0
	const parts = [`${total} attempt${total === 1 ? "" : "s"}`]
	for (const key of ["approved", "declined", "expired", "pending", "under_review"] as const) if (history[key]) parts.push(`${history[key]} ${key.replace("_", " ")}`)
	const last = history.last_declined_at ? ` (last declined: ${day(history.last_declined_at)}${history.last_declined_reason ? `, ${history.last_declined_reason}` : ""})` : ""
	return parts.join(" · ") + last
}

export const KycSummary = ({ token, summary }: { token: string; summary: IKycSummary }) => {
	const { person, document, address, verification, history } = summary
	const name = [person?.first_name, person?.middle_name, person?.last_name].filter(Boolean).join(" ")
	return <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
		<Row style={{ gap: 8, rowGap: 4 }}>
			<strong>Our KYC</strong><StatusBadge value={summary.status || "unknown"} />
			{summary.required === false && <Pill>not required</Pill>}
			<Muted style={{ fontSize: 13 }}>
				{summary.source_label || summary.source}
				{summary.copied_from_holder_uid ? <> · from holder <code>{summary.copied_from_holder_uid}</code></> : null}
				{summary.vendor?.provider ? ` · checked by ${summary.vendor.provider}${summary.vendor.reference_id ? ` (${summary.vendor.reference_id})` : ""}` : ""}
				{verification?.resolved_at ? ` · ${date(verification.resolved_at)}` : ""}
				{verification?.basis === "latest" ? " · latest attempt (none approved)" : ""}
			</Muted>
		</Row>
		{verification?.reason && <Muted style={{ fontSize: 13 }}>Reason: {verification.reason}</Muted>}
		{(person || document || address) && <Columns>
			{person && <section aria-label='Person'><h4>Person</h4><Pairs rows={[
				["Name", dash(name)], ["Gender", dash(person.gender)],
				["Born", <>{dash(person.birth_date_masked)}{person.age != null ? <Muted> · {person.age} y.o.</Muted> : null}</>],
				["Place of birth", dash(person.place_of_birth)]
			]} /></section>}
			{document && <section aria-label='Document'><h4>Document</h4><Pairs rows={[
				["Type", <span style={{ textTransform: "capitalize" }}>{dash(document.type).replaceAll("_", " ")}</span>], ["Country", dash(document.country)],
				["Number", <code>{dash(document.number_masked)}</code>],
				["Issued", day(document.issue_date)],
				["Valid until", <>{day(document.expiry_date)}{document.expired && <Pill $tone='danger'>expired</Pill>}</>],
				["Issued by", dash(document.issuing_authority)]
			]} /></section>}
			{address && <section aria-label='Address'><h4>Address</h4><Pairs rows={[
				["Country", dash(address.country)], ["City", dash(address.city)],
				["Street", dash([address.line1_masked, address.line2_masked].filter(Boolean).join(", "))],
				["Apartment", dash(address.apartment_masked)], ["ZIP", dash(address.zip_masked)]
			]} /></section>}
		</Columns>}
		{summary.notes?.map((note, index) => <Callout key={index} tone={/expired/i.test(note) ? "warn" : "info"}>{note}</Callout>)}
		{history && <Muted style={{ fontSize: 13 }}>History: {historyLine(history)}</Muted>}
		{summary.has_module_session && verification?.module_reference && <Photos key={verification.module_reference} token={token} reference={verification.module_reference} />}
	</div>
}

interface ISession { document_keys?: string[]; documents?: Record<string, { present?: boolean } | undefined> }
const PHOTO_LABEL: Record<string, string> = { passIMG: "Document front", idBackIMG: "Document back", passFaceIMG: "Face on document", selfie: "Selfie", photos: "Other photos" }

/**
 * Photos show the document unmasked: loaded only on an explicit click, kept in component state only, never logged.
 * `raw` is never sent (the raw module answer carries the full number and birth date).
 */
const Photos = ({ token, reference }: { token: string; reference: string }) => {
	const [keys, setKeys] = useState<string[] | null>(null)
	const [images, setImages] = useState<Record<string, string[]>>({})
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState("")
	const gone = "Photos are no longer available."

	const open = async () => {
		setBusy(true); setError("")
		const response = await systemPost<ISession>(token, "system/kyc/module/session", { reference })
		setBusy(false)
		if (response.error || !response.data) return setError(gone)
		const present = (response.data.document_keys ?? []).filter((key) => response.data?.documents?.[key]?.present !== false)
		if (!present.length) return setError("No document photos in this KYC session.")
		setKeys(present)
	}
	const image = async (key: string, index?: number) => {
		const response = await systemPost<{ image_data_uri?: string | null }>(token, "system/kyc/module/session", { reference, image: key, ...(index === undefined ? {} : { index }) })
		if (response.error) throw new Error(gone)
		return response.data?.image_data_uri ?? null
	}
	const show = async (key: string) => {
		setBusy(true); setError("")
		try {
			const found: string[] = []
			if (key === "photos") {
				// ponytail: no count in the session answer, so read indexes until the first empty one (max 10).
				for (let index = 0; index < 10; index++) { const uri = await image(key, index); if (!uri) break; found.push(uri) }
			} else { const uri = await image(key); if (uri) found.push(uri) }
			if (!found.length) setError(`${PHOTO_LABEL[key] ?? key}: no photo.`)
			else setImages((current) => ({ ...current, [key]: found }))
		} catch (cause) { setError(cause instanceof Error ? cause.message : gone) }
		setBusy(false)
	}

	if (!keys) return <Row>
		<Button variant='secondarySmall' disabled={busy} onClick={() => void open()}>{busy ? "Loading…" : "Show document photos"}</Button>
		{error ? <Muted style={{ fontSize: 13 }}>{error}</Muted> : <Muted style={{ fontSize: 13 }}>Unmasked document — open only when you need it.</Muted>}
	</Row>
	return <div style={{ display: "grid", gap: 10 }}>
		<Row>
			{keys.map((key) => <Button key={key} variant='secondarySmall' disabled={busy || !!images[key]} onClick={() => void show(key)}>{PHOTO_LABEL[key] ?? key}</Button>)}
			<Button variant='secondarySmall' onClick={() => { setKeys(null); setImages({}); setError("") }}>Hide photos</Button>
		</Row>
		{error && <Muted style={{ fontSize: 13 }}>{error}</Muted>}
		{Object.entries(images).map(([key, uris]) => uris.map((uri, index) =>
			<figure key={key + index} style={{ margin: 0 }}>
				<figcaption style={{ fontSize: 12.5, color: T.muted, marginBottom: 4 }}>{PHOTO_LABEL[key] ?? key}{uris.length > 1 ? ` ${index + 1}` : ""}</figcaption>
				<img src={uri} alt={PHOTO_LABEL[key] ?? key} style={{ maxWidth: "100%", maxHeight: 420, borderRadius: 8, border: `1px solid ${T.border}` }} />
			</figure>))}
	</div>
}
